<template>
    <div
        :id="id"
        :class="[
            'dt-select',
            { opened: opened === true },
            { 'is-placeholder': isPlaceholder === true },
            { error: isError === true },
        ]"
        :required="required"
        :data-payload="fullPayload"
        ref="select"
        @click="opened = !opened"
    >
        <span class="value" :data-value="value">{{ valueText }}</span>
        <img
            src="@/assets/customizer_icon_dropdown.svg"
            width="24"
            height="24"
            :style="{
                transform: opened ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease',
            }"
        />
        <div v-if="opened" class="select-values">
            <div
                v-for="(option, index) in options"
                :key="index"
                class="select-value"
                @click="changeValue(option)"
            >
                {{ option.text }}
                <span v-if="option.status" class="check"></span>
            </div>
        </div>
    </div>
</template>

<script>
import EventBus from "../event-bus";
import { safeGetJson, safeSetJson } from "../utils/safeStorage";

export default {
    name: "DTSelect",
    props: {
        id: {
            type: String,
            default: "",
        },
        payload: {
            type: String,
            default: "",
        },
        sharePayload: {
            type: Boolean,
            default: false,
        },
        options: {
            type: Array,
            required: true,
            default: () => [],
        },
        placeholder: {
            type: String,
            default: "",
        },
        required: {
            type: Boolean,
            default: false,
        },
        eventBusEmit: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            isError: false,
            fullPayload: this.payload,
            opened: false,
            value: "",
            valueText: "",
            isPlaceholder: false,
        };
    },
    inject: ["pageSettings"],
    mounted() {
        let selected = false;
        this.options.forEach((option) => {
            if (option.selected) {
                selected = true;
                this.value = option.value;
                this.valueText = option.text;
            }
        });

        if (!selected && this.placeholder !== "") {
            this.isPlaceholder = true;
            this.valueText = this.placeholder;
        }

        this.$nextTick(() => {
            EventBus.on("throwErrorForRequiredInputs", this.handleErrorState);
            this.fullPayload = this.getFullPayload();
            this.loadFromLocalStorage();
        });
    },
    beforeUnmount() {
        EventBus.off("throwErrorForRequiredInputs", this.handleErrorState);
    },
    methods: {
        getFullPayload() {
            if (!this.payload || this.payload.trim() === "") {
                return null;
            }

            let element = this.$el;
            while (element && element.parentElement) {
                if (element.parentElement.classList.contains("tab-content")) {
                    const prefix = element.parentElement.getAttribute(
                        "data-payload-prefix"
                    );
                    if (prefix) {
                        return `${prefix}.${this.payload}`;
                    }
                }
                element = element.parentElement;
            }

            return this.payload;
        },
        loadFromLocalStorage() {
            if (!this.fullPayload || !this.pageSettings.storageKey) {
                return;
            }

            const stepValues = safeGetJson(this.pageSettings.storageKey, {});
            const sharedValues = safeGetJson("sharedCustomizerValues", {});
            const keys = this.fullPayload.split(".");
            let current = stepValues;
            let sharedCurrent = sharedValues;

            for (let i = 0; i < keys.length; i++) {
                if (!current[keys[i]]) {
                    current[keys[i]] =
                        i === keys.length - 1
                            ? {
                                  value: this.value || "",
                                  required: this.required,
                              }
                            : {};
                }
                current = current[keys[i]];

                if (sharedCurrent[keys[i]]) {
                    sharedCurrent = sharedCurrent[keys[i]];
                } else if (i < keys.length - 1) {
                    sharedCurrent = {};
                }
            }

            if (sharedCurrent && sharedCurrent.value) {
                this.value = sharedCurrent.value;
                current.value = sharedCurrent.value;
            } else if (current.value) {
                this.value = current.value;
            } else {
                current.value = this.value || "";
            }

            const option = this.options.find((opt) => opt.value === this.value);
            if (option) {
                this.valueText = option.text;
                this.isPlaceholder = false;
            } else if (this.placeholder) {
                this.valueText = this.placeholder;
                this.isPlaceholder = true;
            }

            safeSetJson(this.pageSettings.storageKey, stepValues);
            if (this.sharePayload) {
                safeSetJson("sharedCustomizerValues", sharedValues);
            }
        },
        saveToLocalStorage() {
            if (!this.fullPayload || !this.pageSettings.storageKey) {
                return;
            }

            const stepValues = safeGetJson(this.pageSettings.storageKey, {});
            const sharedValues = safeGetJson("sharedCustomizerValues", {});
            const keys = this.fullPayload.split(".");
            let currentStep = stepValues;
            let currentShared = sharedValues;

            for (let i = 0; i < keys.length - 1; i++) {
                if (!currentStep[keys[i]]) {
                    currentStep[keys[i]] = {};
                }
                currentStep = currentStep[keys[i]];

                if (this.sharePayload) {
                    if (!currentShared[keys[i]]) {
                        currentShared[keys[i]] = {};
                    }
                    currentShared = currentShared[keys[i]];
                }
            }

            currentStep[keys[keys.length - 1]] = {
                ...(currentStep[keys[keys.length - 1]] || {}),
                value: this.value,
                required: this.required,
            };

            if (this.sharePayload) {
                currentShared[keys[keys.length - 1]] = {
                    ...(currentShared[keys[keys.length - 1]] || {}),
                    value: this.value,
                    required: this.required,
                };
            }

            safeSetJson(this.pageSettings.storageKey, stepValues);
            if (this.sharePayload) {
                safeSetJson("sharedCustomizerValues", sharedValues);
            }
        },
        changeValue(option) {
            this.isPlaceholder = false;
            this.value = option.value;
            this.valueText = option.text;

            this.saveToLocalStorage();

            if (this.eventBusEmit) {
                const index = this.options.findIndex(
                    (opt) => opt.value === option.value
                );

                EventBus.emit(this.eventBusEmit, index);
            }

            this.isError = false;
        },
        setOption(index) {
            const option = this.options[index];

            this.isPlaceholder = false;
            this.value = option.value;
            this.valueText = option.text;
        },
        handleErrorState(newState) {
            if (newState === false) {
                this.isError = false;
                return;
            }

            if (this.required && this.value.trim() === "") {
                this.isError = true;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@use "@/assets/customizer.mixins.scss" as *;
.dt-select {
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    column-gap: 5px;
    width: 100%;
    padding: 14.875px 32px;
    border: 2px solid $color-light-gray;
    border-radius: 46px;
    background-color: $color-white;
    transition: border-color 0.3s;
    cursor: pointer;
    @include font-body-m;

    & {
        color: $color-input-active-text;
    }

    @media (max-width: $breakpoint-tablet) {
        padding: 16px 24px;
        border-radius: 36px;
        font-size: 16px;
    }

    &.opened,
    &:focus {
        border-width: 2px;
        border-color: #625f6b;
        outline: none;
    }

    &.error {
        border-color: $color-input-error;
    }

    &.is-placeholder {
        color: $color-placeholder-text;
    }

    .select-values {
        position: absolute;
        z-index: 10;
        top: calc(100% + 8px);
        right: 0;
        left: 0;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: stretch;
        row-gap: 16px;
        padding: 16px;
        background-color: $color-white;
        border: 2px solid $color-light-gray;
        border-radius: 16px;
        box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.05);

        .select-value {
            display: flex;
            flex-wrap: nowrap;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            column-gap: 5px;
            color: $color-new-black;
            cursor: pointer;
            transition: color 0.3s;

            @media (max-width: $breakpoint-tablet) {
                font-size: 16px;
            }

            &:hover {
                color: $color-input-active-text;
            }

            .check {
                display: inline-block;
                width: 15px;
                height: 15px;
                background-image: url("~@/assets/customizer_icon_check_green.svg");
                background-size: cover;
                background-position: center center;
            }
        }
    }
}
</style>
